import { useRef, useMemo, useState, useEffect } from 'react'
import axios from 'axios'
import { Drawer, notification } from 'antd'
import { Formik, FormikHelpers } from 'formik'
import { Column } from 'react-display-flex'
import HotTable from 'react-handsontable'
import { Form, FormItem, Input, Select, SubmitButton, Switch, DatePicker } from 'formik-antd'

import { convertRowToColumnIfNeeded, filterOption, formatRate, sortByName, tenors } from '../form-functions'
import { drawerAndModalWidth } from '../../../util/guideline'
import { momentDateHandler } from '../../../date/moment-date-handler'
import { parseApiErrorResponse } from '../../../components/forms/utils'
import { config } from '../../../config'
import { validationSchema } from './validation'
import useSWR from 'swr'
import { Tenant } from '../../Tenants/models'
import { fetcher } from '../../../api'
import { getEnvironmentHost } from '../../../util/environment'
import { MarketIndexFormValues, marketIndexTypes } from '../models'
import { messages } from '../messages'

import '../create-or-update-market-index.scss'

const { Option } = Select
const { formatDate, newDate } = momentDateHandler

interface MarketIndexCreateProps {
  isVisible: boolean
  onClose: () => void
  onSuccess: () => void
}

const initialValues: Partial<MarketIndexFormValues> = {
  name: '',
  baseDate: null,
  isBenchmark: false,
  type: 'performance',
  tenantId: null,
}

export const MarketIndexCreateDrawer = ({ isVisible, onClose, onSuccess }: MarketIndexCreateProps) => {
  const { data: tenants } = useSWR<Tenant[]>('/tenants', fetcher)
  const [isRendered, setIsRendered] = useState(false)
  const tableRef = useRef(null)
  const initialRates = useMemo(() => tenors.map((_) => ({ rate: null })), [])

  useEffect(() => {
    setIsRendered(true)
  }, [])

  const onSubmit = async (
    { name, baseDate, isBenchmark, type, tenantId, ...indexes }: MarketIndexFormValues,
    { setSubmitting, resetForm, setErrors }: FormikHelpers<MarketIndexFormValues>,
  ) => {
    const rates = tableRef.current.hotInstance.getData()
    const payload = {
      name,
      baseDate: formatDate({ date: newDate(baseDate), format: config.shortDateFormat }),
      isBenchmark,
      type,
      tenantId,
      indexes: rates.map(([rate], index) => ({ tenor: tenors[index].tenor, value: formatRate({ value: rate }) })),
    }

    try {
      await axios.post('/market-indexes', payload)

      notification.success({ message: messages.common.success, description: messages.marketIndex.create.success })

      onSuccess()
      resetForm()
    } catch (error) {
      const formErrors = parseApiErrorResponse(error)
      setErrors(formErrors)

      notification.error({ message: messages.common.error, description: messages.marketIndex.create.error })
    }

    setSubmitting(false)
  }

  return (
    <Drawer
      title="Create Market Index"
      placement="right"
      onClose={onClose}
      open={isVisible}
      width={drawerAndModalWidth}
      maskClosable={false}
      forceRender
      destroyOnClose
    >
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit} enableReinitialize>
        <Form name="create-or-update-market-index">
          <FormItem name="name" label="Market Index Name" required>
            <Input name="name" placeholder="Market Index Name" aria-label="name" />
          </FormItem>
          <FormItem name="baseDate" label="Base Date" required>
            <DatePicker
              name="baseDate"
              aria-label="baseDate"
              placeholder="Base Date"
              format={config.defaultDateFormat}
            />
          </FormItem>
          <FormItem name="isBenchmark" label="Is Benchmark" required>
            <Switch checkedChildren="Yes" unCheckedChildren="No" name="isBenchmark" aria-label="isBenchmark" />
          </FormItem>
          <FormItem name="type" label="Purpose">
            <Select
              autoFocus
              showSearch
              name="type"
              id="select-type"
              placeholder="Select a purpose"
              optionFilterProp="children"
              filterOption={filterOption}
              aria-label="type"
            >
              {marketIndexTypes.map(({ label, value }) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
          </FormItem>
          <FormItem name="tenantId" label="Tenant">
            <Select
              autoFocus
              showSearch
              name="tenantId"
              id="select-tenant"
              placeholder="Select a tenant"
              optionFilterProp="children"
              filterOption={filterOption}
              aria-label="tenantId"
            >
              {tenants?.sort(sortByName).map(({ domain, name, id }) => (
                <Option key={domain} value={id} title={getEnvironmentHost({ domain })}>
                  {`${name}  ( ${domain} | ${id} )`}
                </Option>
              ))}
            </Select>
          </FormItem>
          {isRendered && (
            <HotTable
              colHeaders={['Rate']}
              rowHeaders={tenors.map(({ label }) => label)}
              data={initialRates}
              startRows={17}
              maxRows={tenors.length}
              className="ratesheet-table"
              rowHeights={40}
              ref={tableRef}
              onBeforePaste={convertRowToColumnIfNeeded}
            />
          )}
          <Column>
            <SubmitButton htmlType="submit" type="primary" disabled={false} aria-label="createMarketIndex">
              Create Market Index
            </SubmitButton>
          </Column>
        </Form>
      </Formik>
    </Drawer>
  )
}
